import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation, Trans } from 'react-i18next';
import {
  Tooltip,
  RadioGroup,
  CheckboxGroup,
  Toggle,
  Dialog,
  DialogButton,
} from '~/notify/settings/__modules__/component';
import {
  PopUpBiz,
  BROWSER_IS_IE,
  BROWSER_IS_SAFARI,
} from '~/notify/settings/biz/PopUpBiz';
import { useToast } from '~/__modules__/toast';
import { useDialog } from '~/__modules__/dialog';

const handleNotificationPermissionClick = function () {
  Notification.requestPermission(() => {
    location.reload();
  });
};

PopUp.propTypes = {
  availableServices: PropTypes.array,
};

export function PopUp({ availableServices }) {
  const { t } = useTranslation();
  const toast = useToast();
  const dialog = useDialog();

  const previewOptions = [
    { value: true, label: t('noti_main_previewmsg') },
    { value: false, label: t('noti_main_hidemsg') },
  ];

  let serviceOptions = [
    {
      value: 'service',
      label: t('service_settings_page_title'),
      tooltip: <Trans i18nKey="noti_main_tooltip_service" />,
      isDefault: true,
    },
    {
      value: 'message',
      label: t('noti_main_popupset_message'),
      tooltip: <Trans i18nKey="noti_main_tooltip3" />,
    },
    {
      value: 'mail',
      label: t('noti_main_popupset_mail'),
      tooltip: <Trans i18nKey="noti_main_tooltip4" />,
    },
  ];

  const [popVisible, setPopVisible] = useState(false); // alert
  const [config, setConfig] = useState(null);

  const onSettingsChange = function (key, value) {
    let settings = Object.assign({}, config.settings, { [key]: value });
    let _config = {
      ...config,
      settings,
    };
    PopUpBiz.save(settings)
      .then(() => {
        toast(t('saved'));
        setConfig(_config);
        localStorage.setItem('settings', JSON.stringify(settings));
      })
      .catch(() => {
        dialog.alert((action) => ({
          title: <Trans i18nKey="error_unknown_title" />,
          description: <Trans i18nKey="error_unknown_description" />,
          [action()]: (close) => {
            close && close();
          },
        }));
      });
  };

  const handleSubscribedServiceChange = function (checkedList = []) {
    if (checkedList && checkedList.length === 0) {
      setPopVisible(true);
    } else {
      const subscribedServices = config.settings.subscribedServices;
      const subscribedServicesKeys = Object.keys(
        config.settings.subscribedServices,
      );

      subscribedServicesKeys.forEach((key) => {
        subscribedServices[key] = checkedList.includes(key);
      });
      let settings = {
        ...config.settings,
        subscribedServices,
      };
      let _config = {
        ...config,
        settings,
      };
      PopUpBiz.save(settings)
        .then(() => {
          toast(t('saved'));
          setConfig(_config);
          localStorage.setItem('settings', JSON.stringify(settings));
        })
        .catch(() => {
          dialog.alert((action) => ({
            title: <Trans i18nKey="error_unknown_title" />,
            description: <Trans i18nKey="error_unknown_description" />,
            [action()]: (close) => {
              close && close();
            },
          }));
        });
    }
  };

  useEffect(() => {
    PopUpBiz.init().then((result) => {
      setConfig(result);
    });
  }, []);

  let subscribed = true; // pc弹窗提醒 开关 ON/OFF
  let contentPreviewed = true; // 预览内容
  let selectedServices = []; // 已选择的服务

  if (config) {
    subscribed = config.settings.subscribed;
    contentPreviewed = config.settings.contentPreviewed;
    serviceOptions = serviceOptions.filter((item) => {
      return availableServices.includes(`${item.value}_web`) || item.isDefault;
    });
    selectedServices = serviceOptions
      .filter((item) => {
        let key = item.value;
        return config.settings.subscribedServices[key];
      })
      .map((item) => item.value);
  }

  return (
    <>
      {config && (
        <li className="item">
          <div className="title_area">
            <h4 className="title">
              <span className="text">{t('noti_main_popupset')}</span>
              <Tooltip>{t('noti_main_tooltip2')}</Tooltip>
            </h4>
            {BROWSER_IS_IE && (
              <p className="alert">
                {t('noti_main_guide4')}
                <br />
                {t('noti_main_guide5')}
              </p>
            )}
            {!BROWSER_IS_IE && config.notificationPermission() === 'default' && (
              <p className="desc">
                {t('noti_main_guide1')}
                <br />
                {BROWSER_IS_SAFARI && t('noti_main_guide2')}
              </p>
            )}
            {!BROWSER_IS_IE && config.notificationPermission() === 'denied' && (
              <>
                <p className="alert">
                  <Trans i18nKey="noti_main_guide3" />
                </p>
                <p className="desc">
                  {BROWSER_IS_SAFARI && t('noti_main_guide2')}
                </p>
              </>
            )}
            {BROWSER_IS_SAFARI &&
              config.notificationPermission() === 'granted' && (
                <p className="desc">
                  {BROWSER_IS_SAFARI && t('noti_main_guide2')}
                </p>
              )}
          </div>

          <div className="task_area">
            {!BROWSER_IS_IE && config.notificationPermission() === 'default' && (
              <button
                type="button"
                className="lw_btn"
                onClick={handleNotificationPermissionClick}
              >
                {t('noti_main_setnoti')}
              </button>
            )}

            {!BROWSER_IS_IE &&
              config.notificationPermission() === 'granted' && (
                <Toggle
                  value={subscribed}
                  label={t('noti_main_popupset')}
                  onChange={() => onSettingsChange('subscribed', !subscribed)}
                />
              )}
          </div>
          {!BROWSER_IS_IE && config.notificationPermission() === 'granted' && (
            <ul
              className="sub_list"
              style={{ display: subscribed ? 'block' : 'none' }}
            >
              <li className="item">
                <RadioGroup
                  onChange={(value) =>
                    onSettingsChange('contentPreviewed', value)
                  }
                  value={contentPreviewed}
                  options={previewOptions}
                />
              </li>
              <li
                className="item"
                style={{ display: serviceOptions.length ? 'flex' : 'none' }}
              >
                <CheckboxGroup
                  onChange={handleSubscribedServiceChange}
                  value={selectedServices}
                  options={serviceOptions}
                />
              </li>
            </ul>
          )}
          {popVisible && (
            <Dialog.Alert
              title={t('noti_main_alert1')}
              onClose={() => setPopVisible(false)}
            >
              <DialogButton.Action onSelect={(close) => close()}>
                {t('button_confirm')}
              </DialogButton.Action>
            </Dialog.Alert>
          )}
        </li>
      )}
    </>
  );
}
